/**
 * La liste des affaires, sur le modèle de la référence.
 *
 * **Un bandeau blanc qui ne défile pas**, puis la liste. La maquette sépare
 * ainsi le titre et les outils du contenu : ce qui sert à filtrer reste sous la
 * main quand on parcourt soixante lignes.
 *
 * **Des lignes en grille, non un tableau.** Les colonnes de la référence ont
 * des largeurs fixes et une largeur minimale de 1180 px : la liste défile
 * horizontalement dans son cadre plutôt que d'écraser l'intitulé. Un `<table>`
 * répartissait la place au jugé, et l'intitulé perdait à chaque fois.
 *
 * **Paginée par curseur** — décision `0014`. « Charger la suite » plutôt qu'un
 * défilement infini : un métreur qui cherche une affaire veut savoir où il en
 * est, et un défilement infini lui retire ce repère.
 *
 * Ce que la référence montre et que la plateforme ne tient pas : le client
 * (aucune colonne), le verrou du classeur (il vit dans le schéma de l'affaire,
 * hors du plan de contrôle), et les affaires épinglées (rien ne les épingle).
 * Ces colonnes sont absentes plutôt que vides.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { api, ErreurApi, type Page } from "../api/client";
import { CATEGORIES, type AffaireVue } from "../api/types";
import { Chargement, Echec, Vide } from "../composants/etats";
import { Etat } from "../composants/etiquettes";
import { quand } from "../composants/formats";
import { useDonnees } from "../composants/donnees";
import { Lien, naviguer, useRequete } from "../routeur";

/** Les quatre catégories que la référence résume sur chaque ligne.
 *
 * Pas les six : la pastille sert à voir d'un coup d'œil si l'affaire est
 * chiffrable, et le cadre de sortie comme la maquette IFC ne conditionnent pas
 * un chiffrage. Six pastilles de 20 px les rendraient toutes illisibles. */
const RESUMEES = ["CCTP", "PLAN_DWG", "PLAN_PDF", "DPGF"] as const;

const ETATS = ["READY", "PROVISIONING", "FAILED", "ARCHIVED"] as const;

export function Affaires() {
  const [suite, poserSuite] = useState<AffaireVue[]>([]);
  const [curseur, poserCurseur] = useState<string | null>(null);
  const [chargeLaSuite, poserChargeLaSuite] = useState(false);
  const [echecSuite, poserEchecSuite] = useState("");
  const [dense, poserDense] = useState(() => {
    try {
      return window.localStorage.getItem("mo.densite") !== "confort";
    } catch {
      return true;
    }
  });

  const requete = useRequete();
  // **Le filtre est envoyé au portail, non appliqué ici.** Filtrer les seules
  // lignes déjà chargées ferait mentir la pagination : « aucun résultat » pour
  // une affaire présente deux pages plus loin.
  const terme = requete.get("q") ?? "";
  const etat = requete.get("etat") ?? "";
  const filtre =
    (terme ? `&q=${encodeURIComponent(terme)}` : "") +
    (etat ? `&etat=${encodeURIComponent(etat)}` : "");

  const premiere = useDonnees<Page<AffaireVue>>(
    () => api.lire<Page<AffaireVue>>(`/api/v1/affaires?taille=25${filtre}`),
    [filtre],
  );

  // Une recherche neuve repart de zéro : garder les pages de la précédente
  // mêlerait deux résultats sans que rien ne le dise.
  useEffect(() => {
    poserSuite([]);
    poserCurseur(null);
  }, [filtre]);

  const changerDensite = (versDense: boolean) => {
    poserDense(versDense);
    try {
      window.localStorage.setItem("mo.densite", versDense ? "dense" : "confort");
    } catch {
      /* stockage refusé : la densité vaut pour cette session */
    }
  };

  const charger = useCallback(async () => {
    const depuis = curseur ?? premiere.donnees?.suite;
    if (!depuis) return;
    poserChargeLaSuite(true);
    poserEchecSuite("");
    try {
      const page = await api.lire<Page<AffaireVue>>(
        `/api/v1/affaires?taille=25${filtre}&curseur=${encodeURIComponent(depuis)}`,
      );
      poserSuite((deja) => [...deja, ...page.donnees]);
      poserCurseur(page.suite);
    } catch (exc) {
      // **Un échec de page se dit.** Sans `catch`, il se confondait avec une
      // fin de liste — le métreur concluait à tort qu'une affaire n'existe pas.
      poserEchecSuite(
        exc instanceof ErreurApi ? exc.message
          : "La suite ne s'est pas chargée. Réessayez.");
    } finally {
      poserChargeLaSuite(false);
    }
  }, [curseur, filtre, premiere.donnees?.suite]);

  const affaires = [...(premiere.donnees?.donnees ?? []), ...suite];
  const encore = curseur ?? premiere.donnees?.suite;
  const comptes = affaires.reduce<Record<string, number>>((tas, a) => {
    tas[a.state] = (tas[a.state] ?? 0) + 1;
    return tas;
  }, {});

  return (
    <div className="ecran">
      <div className="ecran__tete">
        <div className="rangee rangee--espacee rangee--bas">
          <div>
            <h1 className="ecran__titre">Affaires</h1>
            <div className="ecran__compte">
              {affaires.length === 0
                ? "Aucune affaire chargée"
                : Object.entries(comptes)
                    .map(([e, n]) => `${n} ${MOTS[e] ?? e.toLowerCase()}`)
                    .join(" · ")}
            </div>
          </div>
          <Lien vers="/affaires/nouvelle" className="bouton bouton--primaire">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Démarrer une affaire
          </Lien>
        </div>

        <div className="outils">
          <label className="champ-recherche">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              type="search"
              defaultValue={terme}
              placeholder="Référence, intitulé…"
              aria-label="Filtrer les affaires"
              onChange={(evenement) => {
                const valeur = evenement.target.value.trim();
                // **Remplacer, non empiler.** Une frappe n'est pas une
                // destination : taper « 30429 » laissait cinq crans, et
                // *précédent* remontait la recherche lettre à lettre.
                naviguer("/affaires" + parametres({ q: valeur, etat }), true);
              }}
            />
          </label>

          <select
            value={etat}
            aria-label="Filtrer par statut"
            onChange={(evenement) =>
              naviguer(
                "/affaires" + parametres({ q: terme, etat: evenement.target.value }),
                true)
            }
          >
            <option value="">Statut : tous</option>
            {ETATS.map((e) => (
              <option key={e} value={e}>{MOTS[e] ?? e}</option>
            ))}
          </select>

          {/* La densité est un réglage de confort, pas une donnée : elle vit
              dans le navigateur de celui qui la choisit. */}
          <div className="segments" role="group" aria-label="Densité">
            <button type="button" aria-pressed={dense}
                    className={dense ? "segments__actif" : ""}
                    onClick={() => changerDensite(true)}>Dense</button>
            <button type="button" aria-pressed={!dense}
                    className={!dense ? "segments__actif" : ""}
                    onClick={() => changerDensite(false)}>Confort</button>
          </div>
        </div>
      </div>

      <div className="ecran__corps">
        {premiere.charge && !premiere.donnees ? (
          <Chargement lignes={5} />
        ) : premiere.erreur ? (
          <Echec erreur={premiere.erreur} reessayer={premiere.recharger} />
        ) : affaires.length === 0 ? (
          <div className="carte">
            <Vide
              titre={terme ? `Aucune affaire pour « ${terme} »` : "Aucune affaire"}
              aide={
                terme || etat
                  ? "La recherche porte sur la référence et l'intitulé."
                  : "Créez-en une, ou reprenez un dossier existant."
              }
              action={
                terme || etat ? (
                  <Lien vers="/affaires" className="bouton">Effacer les filtres</Lien>
                ) : (
                  <Lien vers="/affaires/nouvelle" className="bouton bouton--primaire">
                    Démarrer une affaire
                  </Lien>
                )
              }
            />
          </div>
        ) : (
          <>
            <ParStatut affaires={affaires} dense={dense} />
            <div className="ecran__pied">
              <span>
                Naviguez avec ↑ ↓, ouvrez avec ↵. {affaires.length} affaire
                {affaires.length > 1 ? "s" : ""} chargée
                {affaires.length > 1 ? "s" : ""}
                {encore ? ", d'autres suivent." : "."}
              </span>
              {encore && (
                <button type="button" className="bouton" onClick={() => void charger()}
                        disabled={chargeLaSuite}>
                  {chargeLaSuite ? "Chargement…"
                    : echecSuite ? "Réessayer" : "Charger la suite"}
                </button>
              )}
              {echecSuite && (
                <span className="discret" role="alert">{echecSuite}</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const MOTS: Record<string, string> = {
  READY: "prêtes",
  PROVISIONING: "en préparation",
  FAILED: "en échec",
  ARCHIVED: "archivées",
};

/** L'adresse d'une liste filtrée, sans paramètre vide. */
function parametres(champs: Record<string, string>): string {
  const requete = new URLSearchParams();
  for (const [nom, valeur] of Object.entries(champs)) {
    if (valeur) requete.set(nom, valeur);
  }
  const texte = requete.toString();
  return texte ? `?${texte}` : "";
}

/** Les affaires groupées par statut, chaque groupe repliable.
 *
 * **Une liste à plat mêle ce qui demande une action et ce qui n'en demande
 * plus.** Une affaire archivée et une affaire en échec n'appellent pas le même
 * geste que trois affaires prêtes ; les aligner oblige à lire la colonne
 * « Statut » ligne par ligne pour retrouver celles qui comptent.
 *
 * **Les groupes vides ne s'affichent pas.** Un accordéon « En échec (0) » est
 * une ligne de bruit ; son absence dit la même chose, en mieux.
 *
 * L'ouverture suit ce que l'état veut dire : prêtes et en échec ouvertes — on
 * y travaille, ou il faut y aller — préparation et archivées repliées. Et
 * **un groupe seul est ouvert** : filtrer sur « archivées » pour les trouver
 * repliées serait absurde, et le cas se produit aussi sans filtre, dès qu'une
 * recherche ne laisse qu'un statut.
 *
 * **Ce qui est retenu est le choix, non le pli.** La première version ne
 * gardait que les groupes repliés à la main — un ensemble qui ne sait dire
 * qu'« fermé alors qu'il s'ouvrirait ». Un groupe fermé par défaut avait
 * besoin de l'inverse : cliquer sur « Archivée » l'inscrivait comme replié,
 * c'est-à-dire dans l'état où il était déjà, et **le groupe ne s'ouvrait
 * jamais**. On retient donc, par statut, ce que la personne a décidé ; le
 * défaut ne sert que tant qu'elle n'a rien décidé.
 */
const OUVERTS_PAR_DEFAUT = new Set(["READY", "FAILED"]);

/** Où se retient le choix. La clé a changé de nom en changeant de forme : un
 *  tableau de statuts repliés ne se relit pas comme une table de décisions. */
const MEMOIRE = "mo.groupes";
const MEMOIRE_ANCIENNE = "mo.groupes-replies";

/** Relit les décisions retenues, en reprenant celles de l'ancienne forme.
 *
 *  Le stockage peut être refusé (navigation privée) ou contenir n'importe
 *  quoi : dans les deux cas on repart des défauts plutôt que de casser la
 *  liste des affaires, qui est la page d'accueil du portail. */
function decisionsRetenues(): Record<string, boolean> {
  try {
    const garde = window.localStorage.getItem(MEMOIRE);
    if (garde) {
      const lu: unknown = JSON.parse(garde);
      if (lu && typeof lu === "object" && !Array.isArray(lu)) {
        return Object.fromEntries(
          Object.entries(lu as Record<string, unknown>)
            .filter(([, ouvert]) => typeof ouvert === "boolean"),
        ) as Record<string, boolean>;
      }
      return {};
    }
    const ancien: unknown = JSON.parse(
      window.localStorage.getItem(MEMOIRE_ANCIENNE) ?? "null");
    if (Array.isArray(ancien)) {
      // Les statuts jadis repliés à la main le restent : c'était bien une
      // décision, même si sa forme ne savait pas dire l'inverse.
      return Object.fromEntries(
        ancien.filter((e) => typeof e === "string").map((e) => [e, false]));
    }
  } catch {
    /* stockage illisible : les défauts font l'affaire */
  }
  return {};
}

function ParStatut({
  affaires,
  dense,
}: {
  affaires: AffaireVue[];
  dense: boolean;
}) {
  const [decisions, poserDecisions] =
    useState<Record<string, boolean>>(decisionsRetenues);

  const groupes = ETATS
    .map((etat) => ({ etat, lignes: affaires.filter((a) => a.state === etat) }))
    .filter((groupe) => groupe.lignes.length > 0);

  // Un état inattendu ne disparaît pas de l'écran : mieux vaut un groupe qu'on
  // n'avait pas prévu qu'une affaire qu'on ne voit plus.
  const connus = new Set<string>(ETATS);
  for (const affaire of affaires) {
    if (!connus.has(affaire.state)) {
      connus.add(affaire.state);
      groupes.push({
        etat: affaire.state as (typeof ETATS)[number],
        lignes: affaires.filter((a) => a.state === affaire.state),
      });
    }
  }

  /** Inscrit ce que la personne vient de décider pour ce statut.
   *
   *  `ouvert` est l'état **affiché** au moment du clic, défaut compris : sans
   *  lui, basculer depuis un défaut ne saurait pas de quoi partir. */
  function basculer(etat: string, ouvert: boolean) {
    poserDecisions((avant) => {
      const apres = { ...avant, [etat]: !ouvert };
      try {
        window.localStorage.setItem(MEMOIRE, JSON.stringify(apres));
        window.localStorage.removeItem(MEMOIRE_ANCIENNE);
      } catch {
        /* stockage refusé : le choix vaut pour cette session */
      }
      return apres;
    });
  }

  return (
    <div className="pile">
      {groupes.map(({ etat, lignes }) => {
        const defaut = OUVERTS_PAR_DEFAUT.has(etat) || groupes.length === 1;
        const ouvert = decisions[etat] ?? defaut;
        return (
          <section key={etat} className="groupe">
            <button
              type="button"
              className="groupe__tete"
              aria-expanded={ouvert}
              onClick={() => basculer(etat, ouvert)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2" aria-hidden="true"
                   className={ouvert ? "groupe__fleche groupe__fleche--ouverte"
                                     : "groupe__fleche"}>
                <path d="m9 6 6 6-6 6" />
              </svg>
              <Etat genre="affaire" valeur={etat} />
              <span className="groupe__compte">
                {lignes.length} affaire{lignes.length > 1 ? "s" : ""}
              </span>
            </button>
            {ouvert && <Liste affaires={lignes} dense={dense} />}
          </section>
        );
      })}
    </div>
  );
}

/** La liste elle-même : un en-tête, des lignes, et le clavier.
 *
 * **↑ ↓ ↵ fonctionnent vraiment.** Le pied de page l'annonce ; l'annoncer sans
 * le faire serait pire que de se taire. */
function Liste({ affaires, dense }: { affaires: AffaireVue[]; dense: boolean }) {
  const cadre = useRef<HTMLDivElement>(null);
  const [vise, poserVise] = useState(-1);

  useEffect(() => {
    const clavier = (evenement: KeyboardEvent) => {
      const actif = document.activeElement;
      // On ne détourne pas les flèches d'un champ de saisie.
      if (actif instanceof HTMLInputElement || actif instanceof HTMLSelectElement) return;
      // **N'agir que si le focus est dans CETTE liste.** Deux groupes de statut
      // ouverts posaient chacun leur écouteur global, et une flèche les
      // faisait avancer tous les deux — la sélection sautait entre groupes. En
      // liant la navigation au groupe qui a le focus, un seul répond.
      if (!cadre.current?.contains(actif)) return;
      if (evenement.key === "ArrowDown" || evenement.key === "ArrowUp") {
        evenement.preventDefault();
        poserVise((avant) => {
          const pas = evenement.key === "ArrowDown" ? 1 : -1;
          const apres = Math.min(Math.max(avant + pas, 0), affaires.length - 1);
          const ligne = cadre.current
            ?.querySelectorAll<HTMLElement>("[data-ligne]")[apres];
          ligne?.scrollIntoView({ block: "nearest" });
          // Le focus suit la flèche : il reste dans ce groupe, si bien que
          // l'écouteur des autres groupes ne se réveille pas.
          ligne?.focus();
          return apres;
        });
      }
      if (evenement.key === "Enter" && vise >= 0 && affaires[vise]) {
        naviguer(`/affaires/${affaires[vise].affaire_id}`);
      }
    };
    window.addEventListener("keydown", clavier);
    return () => window.removeEventListener("keydown", clavier);
  }, [affaires, vise]);

  return (
    <div className={`liste${dense ? "" : " liste--confort"}`} ref={cadre}>
      <div className="liste__entete">
        <span>Réf.</span>
        <span>Intitulé</span>
        <span>Sources</span>
        <span>Rôle</span>
        <span>Activité</span>
        <span>Statut</span>
      </div>
      {affaires.map((affaire, rang) => (
        <div
          key={affaire.affaire_id}
          data-ligne=""
          tabIndex={0}
          role="link"
          className={`liste__ligne${rang === vise ? " liste__ligne--visee" : ""}`}
          onClick={() => naviguer(`/affaires/${affaire.affaire_id}`)}
          onKeyDown={(evenement) => {
            if (evenement.key === "Enter" || evenement.key === " ") {
              evenement.preventDefault();
              naviguer(`/affaires/${affaire.affaire_id}`);
            }
          }}
        >
          <span className="mono">{affaire.reference}</span>
          <span className="tronque">
            {affaire.label || <span className="faible">Sans intitulé</span>}
          </span>
          <Sources compte={affaire.sources ?? {}} />
          <span className="discret tronque">{affaire.role ?? "—"}</span>
          <span className="discret">{quand(affaire.modifiee_le)}</span>
          <Etat genre="affaire" valeur={affaire.state} />
        </div>
      ))}
    </div>
  );
}

/** La complétude des sources, en quatre pastilles.
 *
 * Une catégorie absente reste visible mais éteinte : c'est le manque qui
 * informe. Ne montrer que ce qui existe demanderait de se souvenir de ce qui
 * devrait être là. */
function Sources({ compte }: { compte: Record<string, number> }) {
  return (
    <span className="sources">
      {RESUMEES.map((clef) => {
        const nombre = compte[clef] ?? 0;
        const libelle =
          CATEGORIES.find((c) => c.clef === clef)?.libelle ?? clef;
        return (
          <span
            key={clef}
            className={`sources__pastille${nombre ? " sources__pastille--pleine" : ""}`}
            title={`${libelle} : ${nombre || "aucune"}`}
          >
            {ABREGE[clef]}
            {nombre > 1 && <sup>{nombre}</sup>}
          </span>
        );
      })}
    </span>
  );
}

const ABREGE: Record<string, string> = {
  CCTP: "CCTP",
  PLAN_DWG: "DWG",
  PLAN_PDF: "PDF",
  DPGF: "DPGF",
};
